/**
 * Firebase Analytics (GA4) — behavioural + RAG-performance monitoring.
 *
 * PRINCIPLE: metadata only. We NEVER send raw queries, answers, or feedback text
 * to GA4 (this is confidential tax data; the full content already lives in Cosmos).
 * We send lengths, flags, counts, latencies and categories so we can monitor the
 * chatbot's behaviour and quality without leaking content.
 *
 * Everything is best-effort and wrapped so analytics can NEVER break the UI.
 * Enable by setting (at build time, since CRA inlines REACT_APP_*):
 *   REACT_APP_ANALYTICS_ENABLED=true
 *   REACT_APP_FIREBASE_API_KEY / _AUTH_DOMAIN / _PROJECT_ID / _STORAGE_BUCKET /
 *   _MESSAGING_SENDER_ID / _APP_ID / _MEASUREMENT_ID
 */
import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  getAnalytics,
  isSupported,
  logEvent,
  setUserId,
  setUserProperties,
  Analytics,
} from 'firebase/analytics';

const ENABLED = process.env.REACT_APP_ANALYTICS_ENABLED === 'true';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

let app: FirebaseApp | null = null;
let analytics: Analytics | null = null;
let initStarted = false;

/** Initialise GA4 once. No-ops if disabled/misconfigured/unsupported. */
export async function initAnalytics(): Promise<void> {
  if (!ENABLED || initStarted) return;
  initStarted = true;
  try {
    if (!firebaseConfig.apiKey || !firebaseConfig.measurementId) {
      console.info('[analytics] disabled: firebase config missing');
      return;
    }
    if (!(await isSupported())) return;
    app = initializeApp(firebaseConfig);
    analytics = getAnalytics(app);
  } catch (e) {
    // never let analytics break the app
    console.warn('[analytics] init failed', e);
  }
}

/** GA4-safe params: primitives only, strings clipped, no free-text content. */
function clean(params: Record<string, unknown>): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(params || {})) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'number' || typeof v === 'boolean') out[k] = v;
    else out[k] = String(v).slice(0, 90); // clip; never send long free text
  }
  return out;
}

/** Fire a custom event. Safe no-op until analytics is ready. */
export function track(event: string, params: Record<string, unknown> = {}): void {
  try {
    if (analytics) logEvent(analytics, event as string, clean(params));
  } catch {
    /* swallow */
  }
}

/** Non-reversible short id from the username so we don't send raw PII as user_id. */
async function hashId(input: string): Promise<string> {
  try {
    const data = new TextEncoder().encode(input);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const hex = Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    return 'u_' + hex.slice(0, 16);
  } catch {
    return 'u_anon';
  }
}

/** Identify the signed-in user by a HASHED id + segmentation properties (role/bot). */
export async function identifyUser(
  username: string | undefined,
  props: { role?: string; default_bot?: string } = {}
): Promise<void> {
  try {
    if (!analytics || !username) return;
    setUserId(analytics, await hashId(username));
    setUserProperties(analytics, clean(props) as Record<string, string>);
  } catch {
    /* swallow */
  }
}

// --- RAG helpers -----------------------------------------------------------
// Backend refusal / clarification phrases (keep in sync with app.py REFUSAL prompt).
const REFUSAL_MARKERS = [
  'do not contain this information',
  "don't have enough information",
  'documents do not contain',
];
const CLARIFY_MARKER = 'i need some clarification';

/** Classify an answer for RAG-health metrics — from text shape only, no content stored. */
export function classifyAnswer(text: string): { refused: boolean; clarification: boolean } {
  const t = (text || '').toLowerCase();
  return {
    refused: REFUSAL_MARKERS.some((m) => t.includes(m)),
    clarification: t.includes(CLARIFY_MARKER),
  };
}
